import { css, Inline, Paragraph, styled } from 'canoe';
import sum from 'lodash/sum';
import React, { useMemo } from 'react';

import { AnalyticQuery } from '@candu-ai/analytics/types';
import { getSeriesColor } from '@candu-ai/analytics/ui';

const StyledText = styled(Paragraph)`
  ${({ theme }) => css`
    font-weight: bold;
    line-height: 1;
    font-size: ${theme.fonts.small.fontSize};
  `}
`;

const StyledNumber = styled(Paragraph)`
  ${({ theme }) => css`
    font-weight: bold;
    line-height: 1;
    font-size: ${theme.fonts.small.fontSize};
  `}
`;

const PlaceholderBlock = styled.div<{ basis?: number }>`
  ${({ theme, basis = 1 }) => css`
    width: calc(${basis} * ${theme.spaces.xs});
    height: 10px;
    margin: ${theme.spaces.xs} 0;
    background-color: ${theme.colors.GlitterGrayL20};
    border-radius: ${theme.borderRadius.regular};
  `}
`;

interface SummaryProps {
  data: AnalyticQuery<any>;
  unit?: '%';
  title: string;
  withTotalPrefix?: boolean;
}

export const Summary = ({ data, unit, title, withTotalPrefix }: SummaryProps) => {
  const [xAxis, yAxis] = data.fields;

  const sumData = useMemo(() => {
    if (!data.metadata) {
      return 0;
    }

    // we need to grab the xAxis for metric (rather than timeseries) to work
    return sum(data.rows.map((r) => r[yAxis?.name || xAxis?.name] as number));
  }, [data]);

  return (
    <Inline align="center" justify="flex-start" space="xs" mt="xxs" mb="normal">
      <StyledText size="short" color="OnyxGray" noMargin>
        {withTotalPrefix ? `Total ${title}` : title}
      </StyledText>
      <StyledNumber size="short" color={getSeriesColor(0)} noMargin>
        {unit === '%' ? `${(sumData ?? 0).toFixed(1)}%` : sumData?.toLocaleString('en-GB')}
      </StyledNumber>
    </Inline>
  );
};

export const SummaryPlaceholder = () => {
  return (
    <Inline align="center" justify="flex-start" space="xs" mt="xxs" mb="normal">
      <PlaceholderBlock basis={10} />
      <PlaceholderBlock basis={2} />
    </Inline>
  );
};
